import { computed, signal, type ReadonlySignal, type Signal } from "@preact/signals-core";
import { AttributeOffsets, type Offset } from "../engine/attributeOffsets";
import { CONFIG_VERSION, defaultRecipe, effectiveConfig, tunedParamsOf, type PredictorConfig, type Recipe } from "../engine/config";
import type { TuneOutcome } from "../engine/autoTune";
import type { LearnedSlope } from "../engine/slopeLearning";
import { withNudgedTarget } from "../engine/tasteNudge";
import { dateOf, daysBetween, type IsoDateTime } from "../engine/dates";
import { formatGrind, grindInputError, parseGrind, plusDirection, snapGrind } from "./grindFormat";
import { loadActiveConfig, resetToDefaults, revertToPrevious, saveRecipe, type ActiveConfig } from "./activeConfig";
import { effectiveGrinder, learnedSlopeFor } from "./slopeService";
import { evaluateNudge, nudgeMessage, nudgeStateFor, undoLatestNudge } from "./nudges";
import { runTuningIfDue, tuningStatus, type TuneRunner, type TuningStatus } from "./tuning";
import { activeShots } from "./calibration";
import { midpoint, roundToDial, type Bean, type Grinder, type Prediction, type Shot, type Taste } from "../engine/model";
import { predict } from "../engine/predictor";
import { asPseudoObservation, type Prior, type Reference } from "../engine/startingPoint";
import { FIRST_RUN_DONE, LAST_BEAN_ID, LAST_GRINDER_ID, getLongSetting, putLongSetting, type ConfigVersion, type Repositories } from "../storage/repositories";
import { formatSeconds, oneDecimal, parseNumber } from "./format";
import { runLearning, type LearnedPair } from "./learningRunner";
import { LAST_BACKUP_AT, backupFilename, backupReminder, deliverBackup, type BackupReminder, type DeliveryEnv, type DeliveryResult } from "./backup";
import { exportAll } from "../storage/exportImport";
import { priorFor, referenceFor } from "./startingPointService";

/**
 * Port of DashboardViewModel.java on Preact signals, extended with the starting-point prior, prediction logging
 * on save, and the launch-time learning run. Framework-free (signals-core runs in Node).
 *
 * Selection rules (unchanged):
 *  - load(): run learning, restore last grinder and bean; if the saved bean is missing or archived fall back to the
 *    most recently used bean on that grinder, then the first active bean.
 *  - selectGrinder(): default the bean to the most recently used bean on the new grinder; else keep; else first.
 *  - every selection change is persisted immediately.
 */
export const SOFT_MIN_TIME_S = 10.0;
export const SOFT_MAX_TIME_S = 60.0;

export type StepField = "dose" | "yield" | "time" | "grind";

/** Stepper sizes and clamping ranges (grind uses the grinder's dial resolution and range). */
export const STEP_LIMITS = {
  dose: { step: 0.1, min: 5, max: 30 },
  yield: { step: 0.1, min: 5, max: 100 },
  time: { step: 0.5, min: 0.5, max: 120 },
} as const;

const CHART_OPEN = "chart_open";
export const INSTALL_HINT_DISMISSED = "hide_install_hint";

/** The install banner shows only in Safari, only when not running standalone, and only until dismissed. */
export function installHintVisible(env: { standalone: boolean; safari: boolean; dismissed: boolean }): boolean {
  return env.safari && !env.standalone && !env.dismissed;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const ROAST_TEXT: Record<string, string> = {
  light: "Light", medium_light: "Medium-light", medium: "Medium", medium_dark: "Medium-dark", dark: "Dark",
};
const PROCESS_TEXT: Record<string, string> = { washed: "Washed", honey: "Honey", natural: "Natural", anaerobic: "Anaerobic" };

export { formatSeconds } from "./format";

export function beanMetaLine(b: Bean | null, today: string): string {
  if (b === null) return "";
  const parts: string[] = [];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(b.roastDate);
  if (m) parts.push(`Roasted ${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`);
  const age = daysBetween(b.roastDate, today);
  if (age >= 0) parts.push(age === 1 ? "1 day" : `${age} days`);
  if (ROAST_TEXT[b.roastLevel]) parts.push(ROAST_TEXT[b.roastLevel] as string);
  if (PROCESS_TEXT[b.processClass]) parts.push(PROCESS_TEXT[b.processClass] as string);
  if (b.decaf) parts.push("Decaf");
  if (b.targetTimeS !== null) parts.push(`Target ${formatSeconds(b.targetTimeS)} s`);
  return parts.join(" · ");
}

export function grinderChipLabel(g: Grinder | null): string {
  if (g === null) return "";
  const words = g.name.trim().split(/\s+/);
  if (words.length < 3) return g.name.trim();
  const last = words[words.length - 1] as string;
  // "La Marzocco Pico" → "Pico"; "Baratza Sette 270Wi" → "Sette" (skip a trailing model number)
  return /\d/.test(last) ? (words[words.length - 2] as string) : last;
}

/** Same rules as the Grinder record's compact constructor, as a user-facing message. Throws RangeError. */
export function validateGrinder(g: Grinder): void {
  if (!g.name.trim()) throw new RangeError("Name is required");
  if (!Number.isFinite(g.minGrind) || !Number.isFinite(g.maxGrind) || g.minGrind >= g.maxGrind) {
    throw new RangeError("Min must be less than max");
  }
  if (!Number.isFinite(g.dialResolution) || g.dialResolution <= 0) throw new RangeError("Dial step must be positive");
  if (!Number.isFinite(g.priorSlopeB) || g.priorSlopeB >= 0) {
    throw new RangeError("Prior slope must be negative (higher setting = coarser = faster)");
  }
  if (g.referenceSetting !== null && (!Number.isFinite(g.referenceSetting) || g.referenceSetting < g.minGrind || g.referenceSetting > g.maxGrind)) {
    throw new RangeError("Reference setting must lie within the dial range");
  }
}

export interface AppServices {
  readonly repos: Repositories;
  readonly config: PredictorConfig;
  /** Current local date-time as fixed-width ISO ("yyyy-MM-ddTHH:mm:ss"). */
  readonly now: () => IsoDateTime;
}

/** What the Settings screen shows for one grinder. */
export interface GrinderReferenceInfo {
  readonly grinder: Grinder;
  readonly reference: Reference;
  /** seed and learned slope */
  readonly slope: LearnedSlope;
}

/** The Settings auto-tuning section. */
export interface TuningInfo {
  readonly activeVersion: string;
  /** newest first */
  readonly versions: ConfigVersion[];
  readonly status: TuningStatus;
  readonly canRevert: boolean;
}

/** A bean whose target time taste feedback has moved (or paused). */
export interface NudgeInfo {
  readonly bean: Bean;
  readonly offsetS: number;
  readonly paused: boolean;
}

/** Calibration sweep: three settings across the dial, finest first. */
export function calibrationSweep(g: Grinder, center: number): number[] {
  const step = Math.max(g.dialResolution, snapGrind(0.15 / Math.abs(g.priorSlopeB), { ...g, dialDirection: "higher_coarser" }));
  const clamp = (x: number) => Math.max(g.minGrind, Math.min(g.maxGrind, x));
  return [clamp(center - step), clamp(center), clamp(center + step)].map((x) => snapGrind(x, g));
}

export class DashboardViewModel {
  readonly grinders = signal<Grinder[]>([]);
  readonly selectedGrinder = signal<Grinder | null>(null);
  readonly beans = signal<Bean[]>([]);
  readonly selectedBean = signal<Bean | null>(null);

  readonly prediction = signal<Prediction | null>(null);
  /** The starting-point prior behind the current prediction (null when no bean is selected). */
  readonly prior = signal<Prior | null>(null);
  /** Every shot for the bean on the grinder, newest first (history list). */
  readonly history = signal<Shot[]>([]);
  /** The shots the predictor used: history minus anything before the grinder's recalibration (chart). */
  readonly activeHistory = signal<Shot[]>([]);
  readonly emptyState = signal("");
  readonly lastShotDelta = signal("");
  /** Pairs learned during the last load(), for the Settings screen. */
  readonly lastLearned = signal<LearnedPair[]>([]);

  // Log-shot form
  readonly doseText = signal("");
  readonly yieldText = signal("");
  readonly timeText = signal("");
  readonly grindText = signal("");
  readonly rating = signal(0); // 0 = none
  readonly taste = signal<Taste | null>(null);
  /** Trend chart expanded? Remembered in settings. */
  readonly chartOpen = signal(false);
  /** Backup reminder state (dot on the settings button, row at the top of Settings). */
  readonly backup = signal<BackupReminder>({ due: false, daysAgo: null, shotsSince: 0 });
  /** The config in force: defaults ← active tuned version ← recipe. */
  readonly active: Signal<ActiveConfig>;
  /** Taste nudge on the selected bean's target (seconds) and whether nudging paused for it. */
  readonly nudgeOffset = signal(0);
  readonly nudgePaused = signal(false);
  /** "Target nudged to 28.5 s — recent shots tasted sour", shown on the card after a nudge until the bean changes. */
  readonly nudgeNote = signal("");
  /** First-run cards: no beans, no shots, never dismissed. */
  readonly firstRunVisible = signal(false);
  /** "Calibrate your Pico: pull three shots at 6.8, 7.5 and 8.2" while the grinder has no shots. */
  readonly calibrationPrompt = signal("");
  /** Outcome of the last automatic tuning run this session (null until one ran). */
  readonly lastTune = signal<TuneOutcome | null>(null);

  /** "Based on 3 shots · target 30 s" (per-bean target where set); "No shots yet · target 27.5 s" at zero. */
  readonly subtitle: ReadonlySignal<string> = computed(() => {
    const p = this.prediction.value;
    if (p === null || this.selectedBean.value === null) return "";
    const target = formatSeconds(this.effectiveConfigForBean.targetTimeS);
    const n = p.validShots;
    return n === 0 ? `No shots yet · target ${target} s` : `Based on ${n} ${n === 1 ? "shot" : "shots"} · target ${target} s`;
  });

  /** "Roasted 28 Sep · 8 days · Light · Natural · Decaf · Target 30 s", empty fields omitted. */
  readonly beanMeta: ReadonlySignal<string> = computed(() => beanMetaLine(this.selectedBean.value, dateOf(this.services.now())));

  /** Short grinder name for the chip: "La Marzocco Pico" → "Pico", "Niche Zero" stays. */
  readonly grinderChip: ReadonlySignal<string> = computed(() => grinderChipLabel(this.selectedGrinder.value));

  readonly timeWarning: ReadonlySignal<string> = computed(() => {
    const t = parseNumber(this.timeText.value);
    if (t !== null && (t < SOFT_MIN_TIME_S || t > SOFT_MAX_TIME_S)) {
      return `Unusual shot time: outside ${SOFT_MIN_TIME_S.toFixed(0)}–${SOFT_MAX_TIME_S.toFixed(0)} s`;
    }
    return "";
  });

  readonly grindWarning: ReadonlySignal<string> = computed(() => {
    const g = this.selectedGrinder.value;
    const x = parseGrind(this.grindText.value, g);
    if (g !== null && x !== null && (x < g.minGrind || x > g.maxGrind)) {
      return `Grind ${formatGrind(x, g)} is outside the ${g.name} range ${formatGrind(g.minGrind, g)}–${formatGrind(g.maxGrind, g)}`;
    }
    return "";
  });

  constructor(private readonly services: AppServices) {
    this.active = signal<ActiveConfig>({ config: services.config, version: CONFIG_VERSION, recipe: defaultRecipe(), params: tunedParamsOf(services.config) });
    this.doseText.value = oneDecimal(services.config.targetDoseG);
    this.yieldText.value = oneDecimal(services.config.targetYieldG);
  }

  /** The active config (tuned parameters and recipe applied). */
  get config(): PredictorConfig { return this.active.value.config; }
  /** Stamped on every new shot: CONFIG_VERSION, or the active tuned version "…+vN". */
  get configVersion(): string { return this.active.value.version; }
  get repos(): Repositories { return this.services.repos; }

  /** The config in force for the selected bean (hand-set target, else recipe target + taste nudge). */
  get effectiveConfigForBean(): PredictorConfig { return effectiveConfig(this.config, this.engineBean(this.selectedBean.value)); }

  /** The bean as the engine sees it: a taste-nudged target applied unless the bean has its own. */
  private engineBean(b: Bean | null): Bean | null {
    return b === null ? null : withNudgedTarget(b, this.nudgeOffset.value, this.config);
  }

  // ------------------------------------------------------------------------------------------- lifecycle

  async load(): Promise<void> {
    const { repos } = this.services;
    await this.reseedIfUnlearned();
    this.active.value = await loadActiveConfig(repos, this.services.config);
    this.lastLearned.value = await runLearning(repos, this.config, this.services.now());

    this.grinders.value = await repos.grinders.findAll();
    this.beans.value = await repos.beans.findActive();
    this.firstRunVisible.value = this.beans.value.length === 0 && (await repos.shots.findAll()).length === 0
      && (await repos.settings.get(FIRST_RUN_DONE)) === null;

    const savedG = await getLongSetting(repos.settings, LAST_GRINDER_ID);
    const g = (savedG !== null ? this.grinderById(savedG) : null) ?? this.grinders.value[0] ?? null;
    this.selectedGrinder.value = g;

    const savedB = await getLongSetting(repos.settings, LAST_BEAN_ID);
    let b = savedB !== null ? this.activeBeanById(savedB) : null;
    if (b === null && g !== null) b = await this.mostRecentBeanOn(g);
    if (b === null) b = this.beans.value[0] ?? null;
    this.selectedBean.value = b;

    if (g !== null) await putLongSetting(repos.settings, LAST_GRINDER_ID, g.id);
    await putLongSetting(repos.settings, LAST_BEAN_ID, b === null ? null : b.id);
    this.chartOpen.value = (await repos.settings.get(CHART_OPEN)) === "1";
    await this.refresh();
    await this.resetFormDefaults();
    await this.refreshBackupReminder();
  }

  async refresh(): Promise<void> {
    const g = this.selectedGrinder.value;
    const b = this.selectedBean.value;
    if (g === null) {
      this.prediction.value = null;
      this.prior.value = null;
      this.history.value = [];
      this.activeHistory.value = [];
      this.emptyState.value = "No grinders configured";
      this.grindText.value = "";
      return;
    }
    if (b === null) {
      this.prediction.value = null;
      this.prior.value = null;
      this.history.value = [];
      this.activeHistory.value = [];
      this.nudgeOffset.value = 0;
      this.nudgePaused.value = false;
      this.calibrationPrompt.value = "";
      this.emptyState.value = "Add a bean to start";
      this.grindText.value = formatGrind(roundToDial(g, midpoint(g)), g);
      return;
    }
    const { repos } = this.services;
    const today = dateOf(this.services.now());
    const nudge = await nudgeStateFor(repos, b.id as number);
    this.nudgeOffset.value = nudge.offsetS;
    this.nudgePaused.value = nudge.paused;
    const bE = this.engineBean(b) as Bean;
    const cfg = effectiveConfig(this.config, bE);
    const gE = await effectiveGrinder(repos, g, today, this.config);
    const allShots = await repos.shots.findByBeanAndGrinder(b.id as number, g.id as number);
    const shots = activeShots(allShots, g);
    const pr = await priorFor(repos, gE, bE, today, cfg);
    this.history.value = allShots;
    this.activeHistory.value = shots;
    this.prior.value = pr;
    const p = predict(gE, bE, shots, today, cfg, asPseudoObservation(pr, cfg), pr.sourceLabel, formatGrind);
    this.prediction.value = p;
    this.emptyState.value = shots.length === 0 ? p.explanation : "";
    this.grindText.value = formatGrind(p.grindSetting, g);

    const latest = await repos.shots.findLatestByGrinder(g.id as number);
    const unused = latest === null || activeShots([latest], g).length === 0;
    this.calibrationPrompt.value = unused
      ? `Calibrate your ${grinderChipLabel(g)}: pull three shots at ${calibrationSweep(gE, p.grindSettingRaw).map((x) => formatGrind(x, g)).join(", ").replace(/, ([^,]*)$/, " and $1")} to learn its dial.`
      : "";
  }

  // ------------------------------------------------------------------------------------------- selection

  async selectGrinder(g: Grinder | null): Promise<void> {
    if (g === null || g === this.selectedGrinder.value) return;
    this.selectedGrinder.value = g;
    await putLongSetting(this.services.repos.settings, LAST_GRINDER_ID, g.id);
    const current = this.selectedBean.value;
    const next = (await this.mostRecentBeanOn(g))
      ?? (current !== null && this.beans.value.some((x) => x.id === current.id) ? current : null)
      ?? this.beans.value[0] ?? null;
    if (next?.id === current?.id) {
      await this.refresh();
    } else {
      await this.selectBean(next);
    }
  }

  async selectBean(b: Bean | null): Promise<void> {
    const changed = b?.id !== this.selectedBean.value?.id;
    this.selectedBean.value = b;
    await putLongSetting(this.services.repos.settings, LAST_BEAN_ID, b === null ? null : b.id);
    this.lastShotDelta.value = "";
    if (changed) this.nudgeNote.value = "";
    await this.refresh();
    if (changed) await this.resetFormDefaults();
  }

  // ------------------------------------------------------------------------------------------- actions

  /** Save a new bean (id must be null), select it and refresh. */
  async addBean(draft: Bean): Promise<Bean> {
    const saved = await this.services.repos.beans.save(draft);
    this.beans.value = await this.services.repos.beans.findActive();
    if (this.firstRunVisible.value) await this.finishFirstRun();
    await this.selectBean(saved);
    return saved;
  }

  /** Update an existing bean (attributes, dates, target …). Keeps the selection on it if it was selected. */
  async updateBean(bean: Bean): Promise<Bean> {
    if (bean.id === null) throw new RangeError("updateBean needs a saved bean");
    const saved = await this.services.repos.beans.save(bean);
    this.beans.value = await this.services.repos.beans.findActive();
    if (this.selectedBean.value?.id === saved.id) {
      this.selectedBean.value = saved.archived ? null : saved;
      if (saved.archived) await this.archiveFallback();
      else await this.refresh();
    }
    return saved;
  }

  /** Bring an archived bean back into the pantry and select it. */
  async unarchiveBean(id: number): Promise<void> {
    const b = await this.services.repos.beans.findById(id);
    if (b === null) return;
    await this.services.repos.beans.save({ ...b, archived: false });
    this.beans.value = await this.services.repos.beans.findActive();
    await this.selectBean(this.beans.value.find((x) => x.id === id) ?? null);
  }

  /** Every bean, archived included, newest roast first (for the manage-beans screen). */
  async allBeans(): Promise<Bean[]> {
    return this.services.repos.beans.findAll();
  }

  /** Edit a logged shot's values. Timestamp and the stamped prediction fields are kept as they were. */
  async updateShot(shot: Shot): Promise<string | null> {
    if (shot.id === null) return "Shot has not been saved";
    if (shot.doseG <= 0) return "Dose must be a positive number";
    if (shot.yieldG <= 0) return "Yield must be a positive number";
    if (shot.timeSeconds <= 0) return "Time must be a positive number of seconds";
    if (!Number.isFinite(shot.grindSetting)) return "Grind setting must be a number";
    await this.services.repos.shots.save({ ...shot, timeSeconds: Math.round(shot.timeSeconds * 10) / 10, notes: shot.notes.trim() });
    this.lastShotDelta.value = "";
    const b = this.selectedBean.value;
    if (b !== null && b.id === shot.beanId) await this.applyTasteFeedback(b);
    await this.refresh();
    return null;
  }

  /** Archive the selected bean; selection moves to the most recent bean on the grinder, else the first active bean. */
  async archiveSelectedBean(): Promise<void> {
    const b = this.selectedBean.value;
    if (b === null) return;
    await this.services.repos.beans.archive(b.id as number);
    this.beans.value = await this.services.repos.beans.findActive();
    await this.archiveFallback();
  }

  private async archiveFallback(): Promise<void> {
    const g = this.selectedGrinder.value;
    const next = (g !== null ? await this.mostRecentBeanOn(g) : null) ?? this.beans.value[0] ?? null;
    await this.selectBean(next);
  }

  /** Validate, persist (stamping the prediction fields), refresh, publish the delta line. */
  async logShot(): Promise<string | null> {
    const g = this.selectedGrinder.value;
    const b = this.selectedBean.value;
    if (g === null || b === null) return "Select a grinder and a bean first";

    const dose = parseNumber(this.doseText.value);
    const yieldG = parseNumber(this.yieldText.value);
    const time = parseNumber(this.timeText.value);
    const grind = parseGrind(this.grindText.value, g);
    if (dose === null || dose <= 0) return "Dose must be a positive number";
    if (yieldG === null || yieldG <= 0) return "Yield must be a positive number";
    if (time === null || time <= 0) return "Time must be a positive number of seconds";
    if (grind === null) return grindInputError(g);

    const before = this.prediction.value;
    const timeTenths = Math.round(time * 10) / 10;
    const r = this.rating.value;
    const shot: Shot = {
      id: null, beanId: b.id as number, grinderId: g.id as number, timestamp: this.services.now(),
      doseG: dose, yieldG, timeSeconds: timeTenths, grindSetting: grind,
      rating: r >= 1 && r <= 5 ? r : null, notes: "",
      predictedGrind: before === null ? null : before.grindSetting,
      predictedSlope: before === null ? null : before.slopeUsed,
      configVersion: this.configVersion, taste: null,
    };
    const pulledFor = this.effectiveConfigForBean;
    await this.services.repos.shots.save({ ...shot, taste: this.taste.value });
    await this.applyTasteFeedback(b);

    await this.refresh();
    this.lastShotDelta.value = this.deltaLine(shot, before, this.prediction.value as Prediction, g, pulledFor);

    await this.resetFormDefaults();
    await this.refreshBackupReminder();
    return null;
  }

  // ------------------------------------------------------------------------------------------- form

  /**
   * Fresh form for the selected bean: dose from this bean's latest shot on any grinder (dose is about the basket,
   * not the grinder), else the recipe dose; yield the target yield; time, rating and taste empty. Grind is set
   * by refresh() to the recommendation.
   */
  async resetFormDefaults(): Promise<void> {
    const b = this.selectedBean.value;
    const latest = b === null ? null : (await this.services.repos.shots.findByBean(b.id as number))[0] ?? null;
    this.doseText.value = oneDecimal(latest?.doseG ?? this.config.targetDoseG);
    this.yieldText.value = oneDecimal(this.config.targetYieldG);
    this.timeText.value = "";
    this.rating.value = 0;
    this.taste.value = null;
  }

  /** Tap a taste segment; tapping the selected one clears it. */
  toggleTaste(t: Taste): void {
    this.taste.value = this.taste.value === t ? null : t;
  }

  /** Step size for a stepper field (grind follows the grinder's dial resolution). */
  stepSize(field: StepField): number {
    if (field === "grind") return this.selectedGrinder.value?.dialResolution ?? 0.1;
    return STEP_LIMITS[field].step;
  }

  /**
   * − / + on a stepper. Steps clamp to the field's range (grind: the grinder's dial range) and round to the field's
   * precision. An empty or unparsable field jumps to its default instead of stepping. Typed values are never clamped,
   * so the soft out-of-range warnings still apply to anything entered by hand.
   */
  stepField(field: StepField, direction: 1 | -1): void {
    const sig = this.fieldSignal(field);
    const current = field === "grind" ? parseGrind(sig.value, this.selectedGrinder.value) : parseNumber(sig.value);
    if (current === null) {
      sig.value = this.formatField(field, this.fieldDefault(field));
      return;
    }
    const [min, max] = this.fieldRange(field);
    const sign = field === "grind" ? plusDirection(this.selectedGrinder.value) : 1;
    const next = Math.max(min, Math.min(max, current + sign * direction * this.stepSize(field)));
    sig.value = this.formatField(field, this.roundField(field, next));
  }

  /** Round a typed value to its field's precision (on blur). Leaves unparsable text alone for validation. */
  normalizeField(field: StepField): void {
    const sig = this.fieldSignal(field);
    const v = field === "grind" ? parseGrind(sig.value, this.selectedGrinder.value) : parseNumber(sig.value);
    if (v !== null) sig.value = this.formatField(field, this.roundField(field, v));
  }

  private fieldSignal(field: StepField) {
    switch (field) {
      case "dose": return this.doseText;
      case "yield": return this.yieldText;
      case "time": return this.timeText;
      case "grind": return this.grindText;
    }
  }

  private fieldRange(field: StepField): [number, number] {
    if (field === "grind") {
      const g = this.selectedGrinder.value;
      return g === null ? [-Infinity, Infinity] : [g.minGrind, g.maxGrind];
    }
    return [STEP_LIMITS[field].min, STEP_LIMITS[field].max];
  }

  private fieldDefault(field: StepField): number {
    const cfg = this.effectiveConfigForBean;
    switch (field) {
      case "dose": return cfg.targetDoseG;
      case "yield": return cfg.targetYieldG;
      case "time": return cfg.targetTimeS;
      case "grind": {
        const g = this.selectedGrinder.value;
        return this.prediction.value?.grindSetting ?? (g ? roundToDial(g, midpoint(g)) : 0);
      }
    }
  }

  private roundField(field: StepField, v: number): number {
    if (field === "grind") {
      const g = this.selectedGrinder.value;
      return g === null ? v : snapGrind(v, g);
    }
    return Math.round(v * 10) / 10;
  }

  private formatField(field: StepField, v: number): string {
    const g = this.selectedGrinder.value;
    return field === "grind" && g !== null ? formatGrind(v, g) : oneDecimal(v);
  }

  // ------------------------------------------------------------------------------------------- calibration

  /** "Grinder recalibrated": shots before now stop influencing predictions on this grinder; nothing is deleted. */
  async recalibrateGrinder(g: Grinder): Promise<Grinder> {
    return this.saveGrinder({ ...g, calibratedSince: this.services.now() });
  }

  /** Undo a recalibration: every shot on the grinder counts again. */
  async undoRecalibration(g: Grinder): Promise<Grinder> {
    return this.saveGrinder({ ...g, calibratedSince: null });
  }

  // ------------------------------------------------------------------------------------------- backup

  /** Recompute the reminder from settings and the shots logged since the last backup. */
  async refreshBackupReminder(): Promise<void> {
    const last = await this.services.repos.settings.get(LAST_BACKUP_AT);
    const shots = await this.services.repos.shots.findAll();
    const since = last === null ? shots.length : shots.filter((s) => s.timestamp > last).length;
    this.backup.value = backupReminder(last, this.services.now(), since);
  }

  /** The backup file to deliver (JSON of everything, named by today's date). */
  async backupFile(): Promise<File> {
    const now = this.services.now();
    const backup = await exportAll(this.services.repos, now);
    return new File([JSON.stringify(backup, null, 2)], backupFilename(now), { type: "application/json" });
  }

  /** Called when an export completed (shared or downloaded): remember it and clear the reminder. */
  async recordBackup(): Promise<void> {
    await this.services.repos.settings.put(LAST_BACKUP_AT, this.services.now());
    await this.refreshBackupReminder();
  }

  /** Build, deliver and record a backup. A dismissed share sheet records nothing. */
  async exportBackup(env: DeliveryEnv): Promise<DeliveryResult> {
    const result = await deliverBackup(await this.backupFile(), env);
    if (result !== "cancelled") await this.recordBackup();
    return result;
  }

  // ------------------------------------------------------------------------------------------- chrome

  /** Show / hide the trend chart; remembered in settings. */
  async toggleChart(): Promise<void> {
    this.chartOpen.value = !this.chartOpen.value;
    await this.services.repos.settings.put(CHART_OPEN, this.chartOpen.value ? "1" : "0");
  }

  /** Whether the install banner should show in this environment (dismissal read from IndexedDB settings). */
  async installHintVisible(env: { standalone: boolean; safari: boolean }): Promise<boolean> {
    const dismissed = (await this.services.repos.settings.get(INSTALL_HINT_DISMISSED)) !== null;
    return installHintVisible({ ...env, dismissed });
  }

  async dismissInstallHint(): Promise<void> {
    await this.services.repos.settings.put(INSTALL_HINT_DISMISSED, "1");
  }

  async saveGrinder(g: Grinder): Promise<Grinder> {
    validateGrinder(g);
    const saved = await this.services.repos.grinders.save(g);
    this.grinders.value = await this.services.repos.grinders.findAll();
    const current = this.selectedGrinder.value;
    if (current === null) {
      this.selectedGrinder.value = saved;
      await putLongSetting(this.services.repos.settings, LAST_GRINDER_ID, saved.id);
      await this.refresh();
    } else if (current.id === saved.id) {
      this.selectedGrinder.value = saved;
      await this.refresh();
    }
    return saved;
  }

  async deleteShot(shot: Shot): Promise<void> {
    if (shot.id === null) return;
    await this.services.repos.shots.delete(shot.id);
    this.lastShotDelta.value = "";
    await this.refresh();
    await this.refreshBackupReminder();
  }

  // ------------------------------------------------------------------------------------------- settings screen

  /** Per-grinder reference setting as of today, from the other beans' history. */
  async grinderReferences(): Promise<GrinderReferenceInfo[]> {
    const today = dateOf(this.services.now());
    const out: GrinderReferenceInfo[] = [];
    for (const g of this.grinders.value) {
      const slope = await learnedSlopeFor(this.services.repos, g, today, this.config);
      const gE = slope.slope === g.priorSlopeB ? g : { ...g, priorSlopeB: slope.slope };
      out.push({ grinder: g, reference: await referenceFor(this.services.repos, gE, today, this.config), slope });
    }
    return out;
  }

  async learnedOffsets(): Promise<Offset[]> {
    return (await this.services.repos.offsets.load()).entries();
  }

  /** Back to the seed offsets; the ledger is cleared so learning re-runs from all qualifying beans on next load. */
  async resetOffsetsToSeeds(): Promise<void> {
    await this.services.repos.offsets.resetToSeeds();
    await this.services.repos.learning.clear();
    this.lastLearned.value = await runLearning(this.services.repos, this.config, this.services.now());
    await this.refresh();
  }

  // ------------------------------------------------------------------------------------------- self-improvement

  /** Save the recipe (dose, yield, time window) and recompute everything with it. Throws RangeError on bad input. */
  async saveRecipe(r: Recipe): Promise<void> {
    await saveRecipe(this.services.repos, r);
    await this.reloadConfig();
    await this.resetFormDefaults();
  }

  /** Run automatic tuning when it is due (40 valid shots, then every 20). Never throws; the app keeps its config. */
  async autoTuneIfDue(runner?: TuneRunner): Promise<TuneOutcome | null> {
    try {
      const outcome = await runTuningIfDue(this.services.repos, this.config, this.services.now(), runner);
      if (outcome !== null) {
        this.lastTune.value = outcome;
        if (outcome.changed) await this.reloadConfig();
      }
      return outcome;
    } catch {
      return null;
    }
  }

  async tuningInfo(): Promise<TuningInfo> {
    const versions = (await this.services.repos.configVersions.findAll()).reverse();
    return { activeVersion: this.configVersion, versions, status: await tuningStatus(this.services.repos, this.config),
             canRevert: this.configVersion !== CONFIG_VERSION };
  }

  async revertTuning(): Promise<void> {
    await revertToPrevious(this.services.repos, this.services.now(), this.services.config);
    await this.reloadConfig();
  }

  async resetTuning(): Promise<void> {
    await resetToDefaults(this.services.repos, this.services.now(), this.services.config);
    await this.reloadConfig();
  }

  /** Undo the latest taste nudge on the selected bean. */
  async undoNudge(): Promise<void> {
    const b = this.selectedBean.value;
    if (b === null) return;
    await undoLatestNudge(this.services.repos, b.id as number);
    this.nudgeNote.value = "";
    await this.refresh();
  }

  /** Beans whose target taste feedback has moved or paused (Settings). */
  async nudgedBeans(): Promise<NudgeInfo[]> {
    const out: NudgeInfo[] = [];
    for (const bean of await this.services.repos.beans.findAll()) {
      const st = await nudgeStateFor(this.services.repos, bean.id as number);
      if (st.offsetS !== 0 || st.paused) out.push({ bean, offsetS: st.offsetS, paused: st.paused });
    }
    return out;
  }

  async finishFirstRun(): Promise<void> {
    await this.services.repos.settings.put(FIRST_RUN_DONE, "1");
    this.firstRunVisible.value = false;
  }

  private async reloadConfig(): Promise<void> {
    this.active.value = await loadActiveConfig(this.services.repos, this.services.config);
    await this.refresh();
  }

  /** Evaluate taste feedback on a bean after a shot was saved or edited; a nudge sets the card note. */
  private async applyTasteFeedback(b: Bean): Promise<void> {
    const made = await evaluateNudge(this.services.repos, b, this.services.now(), this.config);
    if (made !== null) {
      const st = await nudgeStateFor(this.services.repos, b.id as number);
      this.nudgeNote.value = nudgeMessage(made, this.config, st.offsetS);
    }
  }

  // ------------------------------------------------------------------------------------------- helpers

  /**
   * If the stored offsets table has never been learned from (all n_beans == 0) but differs from the current seeds,
   * the seeds themselves changed in a newer build: adopt them. Learned values are never overwritten.
   */
  private async reseedIfUnlearned(): Promise<void> {
    const stored = await this.services.repos.offsets.load();
    const seeds = AttributeOffsets.seeds();
    if (stored.isUnlearned() && !stored.sameAs(seeds)) {
      await this.services.repos.offsets.resetToSeeds();
      await this.services.repos.learning.clear();
    }
  }

  private deltaLine(shot: Shot, before: Prediction | null, after: Prediction, g: Grinder, cfg: PredictorConfig): string {
    const tNorm = (shot.timeSeconds * cfg.targetYieldG) / shot.yieldG;
    const delta = tNorm - cfg.targetTimeS;
    const ran = Math.abs(delta) < 0.5
      ? "Shot ran on target"
      : `Shot ran ${Math.abs(delta).toFixed(1)} s ${delta > 0 ? "slow" : "fast"}`;
    const rec = before === null
      ? `recommendation ${formatGrind(after.grindSetting, g)}`
      : `recommendation ${formatGrind(before.grindSetting, g)} → ${formatGrind(after.grindSetting, g)}`;
    return `${ran} · ${rec}`;
  }

  private async mostRecentBeanOn(g: Grinder): Promise<Bean | null> {
    const latest = await this.services.repos.shots.findLatestByGrinder(g.id as number);
    return latest === null ? null : this.activeBeanById(latest.beanId);
  }

  private grinderById(id: number): Grinder | null {
    return this.grinders.value.find((x) => x.id === id) ?? null;
  }

  private activeBeanById(id: number): Bean | null {
    return this.beans.value.find((x) => x.id === id) ?? null;
  }
}
